import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert, ScrollView } from 'react-native';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import staffInvitationService from '../services/staffInvitationService';

interface StaffInviteAcceptScreenProps {
  inviteId: string;
  token: string;
  onAcceptSuccess: () => void;
  onBack?: () => void;
  onNavigateToLogin?: () => void;
}

const StaffInviteAcceptScreen: React.FC<StaffInviteAcceptScreenProps> = ({
  inviteId,
  token,
  onAcceptSuccess,
  onBack,
  onNavigateToLogin,
}) => {
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [roleLabel, setRoleLabel] = useState<string>('Staff');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const invite = await staffInvitationService.getInvite(inviteId);
        if (!invite) {
          setError('Invite not found or expired.');
          return;
        }
        if (invite.token !== token) {
          setError('Invalid or expired invite link.');
          return;
        }
        if (invite.status !== 'pending') {
          setError('This invite has already been accepted or revoked.');
          return;
        }
        setRoleLabel(invite.role === 'site_manager' ? 'Site Manager' : invite.role === 'worker' ? 'Worker' : 'Owner');
        const user = await UnifiedAuthService.getCurrentUser();
        setCurrentUserId(user?.id ?? null);
      } catch {
        setError('Could not load invite.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [inviteId, token]);

  const handleAccept = async () => {
    if (!currentUserId) return;
    setAccepting(true);
    try {
      const result = await staffInvitationService.acceptInvite({ inviteId, token, userId: currentUserId });
      if (result.success) {
        Alert.alert('Invite accepted', `You're now signed in for work as ${roleLabel}.`, [{ text: 'OK', onPress: onAcceptSuccess }]);
      } else {
        Alert.alert('Error', result.error ?? 'Could not accept invite.');
      }
    } catch (e: any) {
      Alert.alert('Error', e?.message ?? 'Could not accept invite.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.subtitle}>Loading invite...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.card}>
          <Text style={styles.errorIcon}>⚠</Text>
          <Text style={styles.errorTitle}>Invalid invite</Text>
          <Text style={styles.errorText}>{error}</Text>
          {onBack && (
            <TouchableOpacity style={styles.primaryButton} onPress={onBack}>
              <Text style={styles.primaryButtonText}>Go back</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <View style={styles.card}>
        <Text style={styles.emoji}>🏢</Text>
        <Text style={styles.title}>Work invitation</Text>
        <Text style={styles.message}>
          You&apos;ve been invited to join a business on BookAPT as <Text style={styles.bold}>{roleLabel}</Text>.
        </Text>
        {!currentUserId ? (
          <>
            <Text style={styles.hint}>Log in or create an account to accept this invitation.</Text>
            {onNavigateToLogin && (
              <TouchableOpacity style={styles.primaryButton} onPress={onNavigateToLogin}>
                <Text style={styles.primaryButtonText}>Log in to accept</Text>
              </TouchableOpacity>
            )}
          </>
        ) : (
          <TouchableOpacity
            style={[styles.primaryButton, accepting && styles.buttonDisabled]}
            onPress={handleAccept}
            disabled={accepting}
          >
            {accepting ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.primaryButtonText}>Accept invitation</Text>}
          </TouchableOpacity>
        )}
        {onBack && (
          <TouchableOpacity style={styles.secondaryButton} onPress={onBack}>
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2F2F7' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F2F2F7' },
  scrollContent: { padding: 24, flexGrow: 1 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 24, alignItems: 'center' },
  emoji: { fontSize: 48, marginBottom: 16 },
  errorIcon: { fontSize: 48, marginBottom: 16 },
  title: { fontSize: 22, fontWeight: '700', color: '#1C1C1E', marginBottom: 12, textAlign: 'center' },
  errorTitle: { fontSize: 22, fontWeight: '700', color: '#1C1C1E', marginBottom: 12, textAlign: 'center' },
  message: { fontSize: 16, color: '#3A3A3C', textAlign: 'center', marginBottom: 12 },
  hint: { fontSize: 14, color: '#8E8E93', textAlign: 'center', marginBottom: 24 },
  errorText: { fontSize: 15, color: '#8E8E93', textAlign: 'center', marginBottom: 24 },
  bold: { fontWeight: '600' },
  subtitle: { fontSize: 14, color: '#8E8E93', marginTop: 12 },
  primaryButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignSelf: 'stretch',
    alignItems: 'center',
    marginBottom: 12,
  },
  primaryButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  secondaryButton: { paddingVertical: 12, alignSelf: 'stretch', alignItems: 'center' },
  secondaryButtonText: { fontSize: 16, color: '#007AFF', fontWeight: '500' },
  buttonDisabled: { opacity: 0.7 },
});

export default StaffInviteAcceptScreen;

